import { useCallback, useEffect, useRef, useState } from 'react';
import { getCollectionSettings } from '../../../../api/collections';
import { getChannelIntegrations } from '../../../../api/channelIntegrations';
import { apiErrorMessage, apiErrorStatus } from '../utils/collectionReminders';

const WHATSAPP_UNOFFICIAL = 'whatsapp_unofficial';

/** Números no oficiales del operador → `{ integrations, integrationsError }`. No lanza. */
async function fetchWhatsappIntegrations() {
  const res = await getChannelIntegrations({ channel: WHATSAPP_UNOFFICIAL });
  return {
    integrations: Array.isArray(res?.integrations) ? res.integrations : [],
    integrationsError: !res || !!res.error,
  };
}

/**
 * Configuración de cobranza del operador (GET billing/collections/settings) y, si se pide,
 * sus números de WhatsApp no oficiales (GET channel-integrations).
 *
 * La usan la pantalla Cobranza y los formularios de envío: de acá salen el huso del
 * operador, si el envío está encendido en el entorno (`delivery`) y el número por defecto.
 *
 * `getChannelIntegrations` no lanza: ante un fallo devuelve `{ error }`. Se reporta aparte
 * (`integrationsError`) porque sin números la configuración igual sirve, y se reintenta
 * aparte (`reloadIntegrations`): recargar todo reiniciaría el formulario de Configuración y
 * borraría lo que el operador estaba editando.
 *
 * Si una recarga de la configuración falla, se conserva la última `view` buena (con `error`
 * informado): reemplazar la pantalla por el error haría perder lo que se estaba editando.
 *
 * @param {{ enabled?: boolean, withIntegrations?: boolean }} options
 * @returns {{ loading: boolean, error: { status: number|null, message: string|null } | null,
 *   view: object|null, integrations: object[], integrationsError: boolean,
 *   integrationsLoading: boolean, reload: () => Promise<void>,
 *   reloadIntegrations: () => Promise<void> }}
 */
export default function useCollectionsSetup({ enabled = true, withIntegrations = false } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    view: null,
    integrations: [],
    integrationsError: false,
    integrationsLoading: false,
  });
  // Solo la última carga escribe: un "Reintentar" rápido no debe pisar con una respuesta vieja.
  // Los números llevan su propia secuencia porque se pueden recargar solos.
  const requestSeq = useRef(0);
  const integrationsSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const intSeq = withIntegrations ? ++integrationsSeq.current : null;
    setState((s) => ({ ...s, loading: true, error: null }));
    const [settings, integrations] = await Promise.all([
      getCollectionSettings().then(
        (res) => ({ data: res.data }),
        (err) => ({ err }),
      ),
      withIntegrations ? fetchWhatsappIntegrations() : Promise.resolve(null),
    ]);
    if (seq !== requestSeq.current) return;
    setState((s) => ({
      ...s,
      loading: false,
      error: settings.err
        ? { status: apiErrorStatus(settings.err), message: apiErrorMessage(settings.err) }
        : null,
      view: settings.data ?? s.view,
      ...(integrations && intSeq === integrationsSeq.current
        ? { ...integrations, integrationsLoading: false }
        : {}),
    }));
  }, [withIntegrations]);

  const reloadIntegrations = useCallback(async () => {
    if (!withIntegrations) return;
    const seq = ++integrationsSeq.current;
    setState((s) => ({ ...s, integrationsLoading: true }));
    const integrations = await fetchWhatsappIntegrations();
    if (seq !== integrationsSeq.current) return;
    setState((s) => ({ ...s, ...integrations, integrationsLoading: false }));
  }, [withIntegrations]);

  useEffect(() => {
    if (enabled) load();
    // Al desmontar (o apagar) se invalida lo que esté en vuelo.
    return () => {
      requestSeq.current += 1;
      integrationsSeq.current += 1;
    };
  }, [enabled, load]);

  return { ...state, reload: load, reloadIntegrations };
}
